import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { SubsectionService } from '../../../shared/services/subsection.service';
import {
  ActiveSubsectionState,
  MalwareScanningStatus,
  OptionInfo,
  OptionUpdateResponse,
  OptionVersion,
  OptionVersionSimple,
  SubsectionNavigationParams,
} from '../../../shared/interfaces';
import { ProjectService } from '../../../shared/services/project.service';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OverlayService } from '../../../shared/services/overlayService';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { EmitterService } from '../../../shared/services/emitter.service';
import { FilesDeletedEvent, OptionCreatedEvent, OptionUpdatedEvent, VersionUpdatedEvent } from '../../../shared/models';
import {
  FileTransferService,
  listenerFileUploadCompleted,
  listenerUploadPendingScan,
  MTMCustomFile,
  SignedURLUploadRequest,
  TransferStatus,
  TransferType,
} from '../../../shared/services/signed-url';
import { AuthService } from '../../../shared/services/auth.service';
import { MTMFileDownload } from '../../../shared/services/signed-url/mtm-file-download';
import { NotificationService } from '../../../shared/services/notification.service';
import {
  SubsectionVersionListComponent,
  SubsectionVersionListResult,
} from '../../../shared/components/subsection-version-list/subsection-version-list.component';
import { FileDisplayNamePipe } from '../../../shared/pipes/file-display-name.pipe';
import { DirectoryService } from '../../../shared/services/directory.service';
import { FileDisplayInfo, SubsectionFileToDelete } from '../../models/file-display-info';
import { ReleaseTreeService } from '../../services/release-tree-service';
import { ReleaseAssetSimple } from '../../models/release';
import { FormHelperService } from '../../../shared/services/form-helper.service';
import { FileUtilsService } from '../../../shared/services/file-utils.service';
import { StringUtilsService } from '../../../shared/services/string-utils.service';

@Component({
  selector: 'subsection-option-edit',
  templateUrl: './subsection-option-edit.component.html',
  styleUrl: './subsection-option-edit.component.scss',
  standalone: false,
})
export class SubsectionOptionEditComponent implements OnInit, OnDestroy {
  @Input() optionId: string = '';
  @Input() topicId: string = '';
  @Input() versionNumber: number = 1;
  @Input() isNewVersion: boolean = false;
  @Input() redirectOnSave: boolean = true;

  private readonly fb = inject(FormBuilder);
  private readonly projectService = inject(ProjectService);
  private readonly subsectionService = inject(SubsectionService);
  private readonly activeModal = inject(NgbActiveModal);
  private readonly overlayService = inject(OverlayService);
  private readonly translatePipe = inject(TranslatePipe);
  private readonly authService = inject(AuthService);
  private readonly transferService = inject(FileTransferService);
  private readonly notificationService = inject(NotificationService);
  private readonly modalService = inject(NgbModal);
  private readonly fileDisplayPipe = inject(FileDisplayNamePipe);
  private readonly directoryService = inject(DirectoryService);
  private readonly releaseTreeService = inject(ReleaseTreeService);
  private readonly stringUtilsService = inject(StringUtilsService);
  private readonly fileUtilsService = inject(FileUtilsService);
  private readonly formHelperService = inject(FormHelperService);

  subsectionState: ActiveSubsectionState = null;
  optionForm = this.fb.group({
    title: [
      '',
      {
        validators: [Validators.required],
      },
    ],
    description: ['', [Validators.maxLength(300)]],
  });
  isLoading: boolean = false;
  currentOption: OptionInfo | Record<string, never> = {};
  originalOption: OptionInfo | null = null;
  currentVersion: OptionVersion = null;
  versions: Record<number, OptionVersion> = {};
  originalVersions: Record<number, OptionVersion> = {};
  currentFiles: any[] = [];
  newFiles: MTMCustomFile[] = [];
  fileDisplayRecord: Record<string, FileDisplayInfo> = {};
  newFileDisplayRecord: Record<string, FileDisplayInfo> = {};
  private readonly ngUnsubscribe = new Subject<void>();
  draggingKey: string = 'option-files';
  filesLoading: boolean = false;
  newStatusCodes = [TransferStatus.NEW, TransferStatus.WAITING];
  hasFilesToUpload: boolean = false;
  originalVersionOrder: OptionVersionSimple[] = [];

  private readonly versionsToDelete: number[] = [];
  private expectedNewVersion: number = 0;
  private readonly filesToDelete: SubsectionFileToDelete[] = [];
  protected readonly MalwareScanningStatus = MalwareScanningStatus;
  protected readonly TransferStatus = TransferStatus;

  // Properties for locking mechanism
  releaseRecord: Record<string, ReleaseAssetSimple> = {};

  constructor() {}

  get versionFilterParam(): number {
    return this.currentVersion?.versionNumber || this.versionNumber;
  }

  ngOnInit() {
    this.subsectionService.subsection$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (state) => {
        this.subsectionState = state;
      },
    });

    listenerFileUploadCompleted.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (file: MTMCustomFile) => {
        this.syncFiles(file.dbFileObject);
        EmitterService.get(OptionUpdatedEvent).emit(this.currentOption);
      },
    });

    listenerUploadPendingScan.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (file: MTMCustomFile) => {
        this.syncFiles(file.dbFileObject);
        EmitterService.get(OptionUpdatedEvent).emit(this.currentOption);
      },
    });

    // Subscribe to release tree service to get release record
    this.releaseTreeService
      .getState()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (state) => {
          this.releaseRecord = state.releaseRecord || {};
        },
      });

    if (this.optionId) {
      this.loadOption();
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  loadOption() {
    this.isLoading = true;
    const projectId = this.projectService.project.id;
    const sectionId = this.subsectionState.section.id.toString();
    const subsectionId = this.subsectionState.subsection.id.toString();

    const requests: Observable<any>[] = [
      this.subsectionService.getOption(projectId, sectionId, subsectionId, this.optionId),
    ];

    if (!this.isNewVersion) {
      requests.push(
        this.subsectionService.getVersion({
          projectId: projectId,
          sectionId: sectionId,
          subsectionId: subsectionId,
          itemId: this.optionId,
          versionNumber: this.versionNumber,
        })
      );
    }

    forkJoin(requests)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (result) => {
          const option = result[0];
          this.optionForm.patchValue({
            title: option.title,
            description: option.description,
          });
          if (option.simpleVersions) {
            option.simpleVersions.sort((v1, v2) => v1.order - v2.order);
          }
          this.originalVersionOrder = [...option.simpleVersions];
          this.currentOption = option;
          if (!this.isNewVersion) {
            this.currentVersion = result[1];
            this.subsectionService.sortFilesByFileOrder(this.currentVersion);
            this.updateFileVM();
            this.versions[this.versionNumber] = this.currentVersion;
            this.originalVersions[this.versionNumber] = JSON.parse(JSON.stringify(this.currentVersion));
            this.currentFiles = this.currentVersion.files;
          } else {
            this.createNewVersion();
          }

          this.originalOption = JSON.parse(JSON.stringify(option));
          this.monitorFiles();
          this.populateNewFileDisplayRecord();
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.overlayService.showError(this.translatePipe.transform('versioning_edit_loadOptionFailed'));
          this.activeModal.dismiss();
        },
      });
  }

  handleFileUpload(files: any) {
    const filesToUpload = files.files;
    if (!filesToUpload) {
      return;
    }

    const filtered = this.fileUtilsService.filterFilesToUpload(filesToUpload);
    if (filtered.length === 0) {
      return;
    }
    this.prepareFilesForUpload(filtered);
  }

  private updateFileVM() {
    this.currentVersion.files.forEach((file) => {
      if (this.fileDisplayRecord[file.id]) {
        return;
      }
      const caption = this.fileDisplayPipe.transform(file.displayName, true);

      this.fileDisplayRecord[file.id] = {
        fileId: file.id,
        displayName: file.displayName,
        caption,
        originalCaption: caption,
        version: this.currentVersion.versionNumber,
      };
    });
  }

  private continueUploading() {
    this.syncNewNamesBeforeUpload();

    const errorUploadedFiles = this.newFiles.filter((file) => file.uploadStatusCode === TransferStatus.ERROR);
    if (this.newFiles.length > 0) {
      this.startVersionedUpload();
    } else if (errorUploadedFiles && errorUploadedFiles.length) {
      this.newFiles = errorUploadedFiles;
      this.startVersionedUpload();
    }
  }

  private syncFiles(file: any) {
    if (!this.optionId) {
      return;
    }

    const isExisting = this.currentFiles.some((f) => f.id === file.id);

    if (!(file.id in this.fileDisplayRecord)) {
      const caption = this.fileDisplayPipe.transform(file.displayName, true);
      this.fileDisplayRecord[file.id] = {
        fileId: file.id,
        displayName: file.displayName,
        caption,
        originalCaption: caption,
        version: this.currentVersion?.versionNumber,
      };
    }
    if (
      !isExisting &&
      file?.entity?.id == this.optionId &&
      file?.entity?.version == this.currentVersion?.versionNumber
    ) {
      this.currentFiles.push(file);
    }
  }

  private areFileNamesValid(): boolean {
    const newNamesValid = this.newFiles.every((file) => {
      const displayInfo = this.newFileDisplayRecord[file.fileLocalId];
      return !!displayInfo.caption.trim();
    });

    const existingNamesValid = this.currentFiles.every((file) => {
      const displayInfo = this.fileDisplayRecord[file.id];
      return !!displayInfo.caption.trim();
    });

    return newNamesValid && existingNamesValid;
  }

  private populateNewFileDisplayRecord() {
    this.newFiles.forEach((file) => {
      if (!(file.fileLocalId in this.newFileDisplayRecord)) {
        let fileNameWithoutExtension = this.fileUtilsService.getFileNameWithoutExtension(file.fileName);
        fileNameWithoutExtension = this.stringUtilsService.slugify(fileNameWithoutExtension);
        this.newFileDisplayRecord[file.fileLocalId] = {
          fileId: file.fileLocalId,
          displayName: file.fileName,
          caption: fileNameWithoutExtension,
          originalCaption: fileNameWithoutExtension,
          version: this.currentVersion?.versionNumber || this.versionNumber,
        };
      }
    });
  }

  private syncNewNamesBeforeUpload() {
    this.newFiles
      .filter((f) => f.uploadStatusCode === TransferStatus.NEW)
      .forEach((file) => {
        const displayInfo = this.newFileDisplayRecord[file.fileLocalId];
        const extension = this.fileUtilsService.getFileExtension(file.fileName);
        if (extension) {
          file.fileName = `${displayInfo.caption}.${extension}`;
        }
      });
  }

  submit() {
    this.formHelperService.checkFormFields(this.optionForm);
    if (this.optionForm.invalid || this.isLoading || !this.areFileNamesValid() || this.isCurrentVersionLocked()) {
      return;
    }

    const projectId = this.projectService.project.id;
    const sectionId = this.subsectionState.section.id.toString();
    const subsectionId = this.subsectionState.subsection.id.toString();
    const topicId = this.subsectionState.activeTopic?.topicId;
    const filesToUpload = this.newFiles.filter((f) => f.uploadStatusCode === TransferStatus.NEW);
    this.hasFilesToUpload = filesToUpload.length > 0;

    this.isLoading = true;
    const value: Partial<OptionInfo> = {
      project: projectId,
      section: sectionId,
      subsection: subsectionId,
      topicId: topicId,
      ...(this.optionId ? this.currentOption : {}),
      ...this.optionForm.value,
    };

    const save$: Observable<OptionUpdateResponse> = this.optionId
      ? this.subsectionService.updateOption(value)
      : this.subsectionService.createOption(value);
    save$.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (result: OptionUpdateResponse) => {
        result.id = result.optionId;

        const optionResult: OptionInfo = {
          ...result,
          id: result.optionId,
          project: result.projectId,
          section: result.sectionId,
          subsection: result.subSectionId,
          topicId: topicId,
        };

        if (!this.currentOption.optionId) {
          this.currentOption = optionResult;
          this.checkFilesToUpload();
        }
        this.monitorFiles();
        if (!result.simpleVersions?.length) {
          this.subsectionService.createNextVersion(projectId, sectionId, subsectionId, result.optionId).subscribe({
            next: () => {
              this.notifySaved(optionResult);
              this.continueUploading();
              this.cleanUp(optionResult);
            },
            error: () => {
              this.overlayService.showError(this.translatePipe.transform('versioning_edit_saveOptionFailed'));
              this.isLoading = false;
            },
          });
        } else {
          this.checkNewVersionNeeded();
        }
      },
      error: () => {
        this.overlayService.showError(this.translatePipe.transform('versioning_edit_saveOptionFailed'));
        this.isLoading = false;
      },
    });
  }

  private notifySaved(result: OptionInfo) {
    EmitterService.get(this.optionId ? OptionUpdatedEvent : OptionCreatedEvent).emit(result);
    EmitterService.get(VersionUpdatedEvent).emit({
      version: this.currentVersion,
    });
  }

  private cleanUp(result: OptionInfo) {
    const successMessage = this.hasFilesToUpload ? 'versioning_edit_uploadStarted' : 'dataSaved';
    this.overlayService.showSuccess(this.translatePipe.transform(successMessage));
    EmitterService.get(this.optionId ? OptionUpdatedEvent : OptionCreatedEvent).emit(result);
    EmitterService.get(VersionUpdatedEvent).emit({
      version: this.currentVersion,
    });

    this.activeModal.close();
    if (this.redirectOnSave) {
      this.subsectionService.navigateSubsection({
        projectId: result.project,
        sectionId: result.section,
        subsectionId: result.subsection,
        topicId: result.topicId,
        optionId: result.optionId,
        versionNumber: this.currentVersion?.versionNumber || this.versionNumber,
      });
    }
  }

  cancel() {
    for (let i = this.newFiles.length - 1; i >= 0; i--) {
      if (this.newFiles[i].uploadStatusCode == TransferStatus.NEW) {
        this.deleteNewFile(this.newFiles[i]);
      }
    }

    if (this.currentFiles.length === 0 || this.isNewVersion) {
      const projectId = this.projectService.project.id;
      const sectionId = this.subsectionState.section.id.toString();
      const subsectionId = this.subsectionState.subsection.id.toString();
      const topicId = this.subsectionState.activeTopic?.topicId;
      const navParams: SubsectionNavigationParams = {
        projectId,
        sectionId,
        subsectionId,
        topicId,
      };

      if (this.optionId) {
        navParams.optionId = this.optionId;

        if (this.originalVersionOrder.length == 1) {
          navParams.versionNumber = this.originalVersionOrder[0].versionNumber;
        }
      }

      if (this.redirectOnSave) {
        this.subsectionService.navigateSubsection(navParams);
      }
    }

    this.activeModal.dismiss();
  }

  deleteNewFile(file: MTMCustomFile) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const fileIndex = this.newFiles.findIndex((f) => f.fileLocalId == file.fileLocalId);
    if (fileIndex > -1) {
      this.newFiles.splice(fileIndex, 1);
    }
    this.transferService.cancelUpload(file);
    delete this.newFileDisplayRecord[file.fileLocalId];
    this.isLoading = false;
  }

  downloadFile(file: any) {
    const downloadItem = new MTMFileDownload();
    downloadItem.name = file.name;
    downloadItem.url = file.signedURL;
    downloadItem.contentType = file.contentType;
    downloadItem.size = file.size;
    downloadItem.displayName = file.displayName;
    this.transferService.downloadFile(downloadItem);
  }

  deleteFile(file: any) {
    this.filesToDelete.push({
      fileId: file.id,
      optionId: this.currentOption.optionId,
      version: this.currentVersion.versionNumber,
    });
    const index = this.currentFiles.findIndex((f) => f.id == file.id);
    this.currentFiles.splice(index, 1);
  }

  switchVersion(versionNumber: number) {
    const done = () => {
      const activeIndex = this.currentOption.simpleVersions.findIndex((v) => v.versionNumber == versionNumber);
      const newActiveVersion = this.currentOption.simpleVersions[activeIndex];
      this.currentOption.simpleVersions.splice(activeIndex, 1);
      this.currentOption.simpleVersions.unshift(newActiveVersion);
      this.currentOption.simpleVersions.forEach((v, index) => {
        v.order = index;
      });
      this.updateFileVM();
    };

    if (this.versions[versionNumber]) {
      const version = this.versions[versionNumber];
      this.currentVersion = version;
      this.currentFiles = version.files;
      done();
    } else {
      this.subsectionService
        .getVersion({
          projectId: this.currentOption.project,
          sectionId: this.currentOption.section,
          subsectionId: this.currentOption.subsection,
          itemId: this.currentOption.optionId,
          versionNumber,
        })
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (version) => {
            this.subsectionService.sortFilesByFileOrder(version);
            this.currentVersion = version;
            this.currentFiles = version.files;
            this.versions[versionNumber] = version;
            done();
          },
        });
    }
  }

  dragFile(event: DragEvent & { dataTransfer?: DataTransfer }, file: any) {
    if (!file) {
      return;
    }

    event.dataTransfer.setData(`${this.draggingKey}_fileId`, file.id);
  }

  dragOver(event: DragEvent) {
    (event as any).preventDefault();
  }

  dropFile(event: DragEvent & { dataTransfer?: any }, file: any) {
    const fileId = event.dataTransfer.getData(`${this.draggingKey}_fileId`);
    const originalIndex = this.currentFiles.findIndex((f) => f.id == fileId);
    const targetIndex = this.currentFiles.findIndex((f) => f.id == file.id);
    const originalFile = this.currentFiles[originalIndex];
    this.currentFiles.splice(originalIndex, 1);
    const newTargetIndex = this.currentFiles.findIndex((f) => f.id == file.id);
    if (originalIndex < targetIndex) {
      this.currentFiles.splice(newTargetIndex + 1, 0, originalFile);
    } else {
      this.currentFiles.splice(newTargetIndex, 0, originalFile);
    }
    originalFile.draggable = false;
    const orderDto = {};
    this.currentFiles.forEach((file, index) => {
      orderDto[index + 1] = file.id; // Returning a value from forEach is ignored. thats why remove return.
    });
  }

  showManageVersions() {
    const modalRef = this.modalService.open(SubsectionVersionListComponent, {
      backdrop: 'static',
      modalDialogClass: 'responsive-modal',
    });
    modalRef.componentInstance.projectId = this.currentOption.project;
    modalRef.componentInstance.sectionId = this.currentOption.section;
    modalRef.componentInstance.subsectionId = this.currentOption.subsection;
    modalRef.componentInstance.itemId = this.currentOption.optionId;
    modalRef.componentInstance.versions = [...(this.currentOption.simpleVersions ?? [])];
    modalRef.componentInstance.option = this.currentOption;
    modalRef.componentInstance.isPreviewOnly = true;
    modalRef.result
      .then((result: SubsectionVersionListResult) => {
        if (!result) {
          return;
        }

        if (result.type === 'reorder') {
          const orderedIds = result.orderedIds ?? [];
          if (orderedIds.length === 0) {
            return;
          }
          const orderedSet = new Set(orderedIds);
          const versionsToDelete: number[] =
            result.deletedIds ??
            this.currentOption.simpleVersions.map((v) => v.versionNumber).filter((v) => !orderedSet.has(v));
          const newVersions = this.currentOption.simpleVersions
            .filter((v) => orderedSet.has(v.versionNumber))
            .sort((a, b) => orderedIds.indexOf(a.versionNumber) - orderedIds.indexOf(b.versionNumber));
          newVersions.forEach((v, index) => {
            v.order = index;
          });
          this.currentOption.simpleVersions = newVersions;
          if (orderedIds.length > 0) {
            this.switchVersion(orderedIds[0]);
          }
          const toDeleteSet = new Set(versionsToDelete);
          this.newFiles.filter((f) => toDeleteSet.has(f.entity.version)).forEach((f) => this.deleteNewFile(f));
          versionsToDelete.forEach((v) => {
            if (!this.versionsToDelete.includes(v)) {
              this.versionsToDelete.push(v);
            }
          });
        }
      })
      .catch((error) => {});
  }

  createNewVersion() {
    const maxVersion =
      this.currentOption.simpleVersions.reduce(
        (prev, current) => (prev < current.versionNumber ? current.versionNumber : prev),
        0
      ) + 1;
    const newVersion: OptionVersion = {
      versionNumber: maxVersion,
      order: maxVersion,
      status: '',
      files: [],
      messages: [],
      votes: [],
      optionId: this.currentOption.optionId,
      project: this.currentOption.project,
      section: this.currentOption.section,
      subsection: this.currentOption.subsection,
      versionName: `Version ${maxVersion}`,
    };
    const newSimpleVersion: OptionVersionSimple = {
      name: `Version ${maxVersion}`,
      versionNumber: maxVersion,
      order: 0,
      status: '',
    };
    this.expectedNewVersion = maxVersion;
    this.currentOption.simpleVersions.forEach((v) => v.order++);
    this.currentOption.simpleVersions.unshift(newSimpleVersion);
    this.versions[maxVersion] = newVersion;
    this.currentVersion = newVersion;
    this.currentFiles = newVersion.files;
    this.isNewVersion = true;
  }

  private checkNewVersionNeeded() {
    if (this.expectedNewVersion > 0) {
      this.subsectionService
        .createNextVersion(
          this.currentOption.project,
          this.currentOption.section,
          this.currentOption.subsection,
          this.currentOption.optionId
        )
        .subscribe({
          next: (version) => {
            const actualVersion = version.versionNumber;
            if (actualVersion != this.expectedNewVersion) {
              this.newFiles.forEach((c) => {
                if (c.entity.version === this.expectedNewVersion) {
                  c.entity.version = actualVersion;
                }
              });
              const newVersion = this.currentOption.simpleVersions.find(
                (v) => v.versionNumber == this.expectedNewVersion
              );
              if (newVersion) {
                newVersion.versionNumber = actualVersion;
              }
            }
            this.checkVersionDifferences();
          },
          error: (error) => {
            this.overlayService.showError(this.translatePipe.transform('versioning_edit_saveOptionFailed'));
            this.isLoading = false;
          },
        });
    } else {
      this.checkVersionDifferences();
    }
  }

  private checkVersionDifferences() {
    const obsList: Array<Observable<any>> = [];
    const reorderList: Array<Observable<any>> = [];

    const orderedIds = this.currentOption.simpleVersions.map((version) => version.versionNumber);
    const previousOrderedIds = this.originalOption.simpleVersions.map((version) => version.versionNumber);
    const versionOrderChanged = !(
      orderedIds.length === previousOrderedIds.length &&
      orderedIds.every((id, index) => id === previousOrderedIds[index])
    );

    let versionOps$: Observable<unknown>;
    if (this.versionsToDelete.length > 0) {
      versionOps$ = this.subsectionService
        .deleteVersions(
          this.currentOption.project,
          this.currentOption.section,
          this.currentOption.subsection,
          this.currentOption.optionId,
          this.versionsToDelete
        )
        .pipe(
          switchMap(() =>
            versionOrderChanged
              ? this.subsectionService.reorderVersions(
                  this.currentOption.project,
                  this.currentOption.section,
                  this.currentOption.subsection,
                  this.currentOption.optionId,
                  orderedIds
                )
              : of(null)
          )
        );
    } else if (versionOrderChanged) {
      versionOps$ = this.subsectionService.reorderVersions(
        this.currentOption.project,
        this.currentOption.section,
        this.currentOption.subsection,
        this.currentOption.optionId,
        orderedIds
      );
    } else {
      versionOps$ = of(null);
    }

    if (this.filesToDelete.length > 0) {
      this.filesToDelete.forEach((f) => {
        obsList.push(
          this.subsectionService.deleteFile(
            this.currentOption.project,
            this.currentOption.section,
            this.currentOption.subsection,
            this.currentOption.optionId,
            f.fileId,
            f.version
          )
        );
      });
    }

    const filesToRename: FileDisplayInfo[] = [];

    Object.entries(this.versions).forEach(([versionNo, version]) => {
      const versionKey = parseInt(versionNo, 10);
      if (this.versionsToDelete.includes(versionKey)) {
        return;
      }
      if (version.files.length === 0) {
        return;
      }

      version.files.forEach((file) => {
        if (this.filesToDelete.some((f) => f.fileId === file.id)) {
          return;
        }

        const displayInfo = this.fileDisplayRecord[file.id];

        if (displayInfo.caption != displayInfo.originalCaption) {
          displayInfo.newDisplayName = displayInfo.displayName.replace(
            displayInfo.originalCaption,
            displayInfo.caption
          );
          filesToRename.push(displayInfo);
        }
      });

      if (versionKey in this.originalVersions) {
        const filesOrder = version.files.map((f) => f.id);
        const originalOrder = this.originalVersions[versionKey].files.map((f) => f.id);

        if (
          !(filesOrder.length === originalOrder.length && filesOrder.every((id, index) => id === originalOrder[index]))
        ) {
          const orderDto: Record<number, string> = {};
          version.files.forEach((file, index) => {
            orderDto[index + 1] = file.id;
          });

          reorderList.push(
            this.subsectionService.reorderVersionFiles(
              this.currentOption.project,
              this.currentOption.section,
              this.currentOption.subsection,
              this.currentOption.optionId,
              versionKey,
              orderDto
            )
          );
        }
      }
    });

    if (filesToRename.length > 0) {
      filesToRename.forEach((file) => {
        obsList.push(this.directoryService.renameFolder(this.currentOption.project, file.fileId, file.newDisplayName));
      });
    }

    const otherOps$ = obsList.length > 0 ? forkJoin(obsList) : of(null);

    versionOps$.pipe(switchMap(() => otherOps$)).subscribe({
      next: () => {
        if (this.filesToDelete.length > 0) {
          EmitterService.get(FilesDeletedEvent).emit(this.filesToDelete);
        }
        this.reorderFiles(reorderList);
      },
      error: () => {
        this.overlayService.showError(this.translatePipe.transform('versioning_edit_saveOptionFailed'));
        this.isLoading = false;
      },
    });
  }

  private reorderFiles(reorderList: Array<Observable<any>>) {
    if (reorderList.length > 0) {
      forkJoin(reorderList).subscribe({
        next: () => {
          this.notifySaved(this.currentOption as OptionInfo);
          this.continueUploading();
          this.cleanUp(this.currentOption as OptionInfo);
          this.isLoading = false;
        },
        error: () => {
          this.overlayService.showError(this.translatePipe.transform('versioning_edit_saveOptionFailed'));
          this.isLoading = false;
        },
      });
    } else {
      this.notifySaved(this.currentOption as OptionInfo);
      this.continueUploading();
      this.cleanUp(this.currentOption as OptionInfo);
      this.isLoading = false;
    }
  }

  //#region upload methods

  private prepareFilesForUpload(files: any[]) {
    const waitingFiles = this.newFiles.filter((f) => f.uploadStatusCode === TransferStatus.WAITING);
    waitingFiles.forEach((file) => {
      const newIndex = this.newFiles.findIndex((f) => f == file);
      if (newIndex > -1) {
        this.newFiles.splice(newIndex, 1);
      }
    });

    for (let i = 0; i < files.length; i++) {
      const file = new File([files[i]], files[i].name, { type: files[i].type });
      if (this.newFiles.findIndex((k) => k.fileName == files[i].name) < 0) {
        const newMTMCustomFile = this.createMTMCustomFile(file, i, false);

        if (this.currentOption && !newMTMCustomFile.entity.id) {
          newMTMCustomFile.entity.id = this.currentOption.optionId;
        }
        newMTMCustomFile.entity.version = this.currentVersion?.versionNumber || this.versionNumber;
        this.newFiles.push(newMTMCustomFile);

        let fileNameWithoutExtension = this.fileUtilsService.getFileNameWithoutExtension(newMTMCustomFile.fileName);
        fileNameWithoutExtension = this.stringUtilsService.slugify(fileNameWithoutExtension);
        this.newFileDisplayRecord[newMTMCustomFile.fileLocalId] = {
          fileId: newMTMCustomFile.fileLocalId,
          displayName: newMTMCustomFile.fileName,
          caption: fileNameWithoutExtension,
          originalCaption: fileNameWithoutExtension,
          version: this.currentVersion?.versionNumber || this.versionNumber,
        };
      }
    }
  }

  private checkFilesToUpload() {
    this.newFiles.forEach((file) => {
      if (!file.entity.id && this.currentOption) {
        file.entity.id = this.currentOption?.optionId;
      }
      if (!file.entity.version) {
        file.entity.version = this.currentVersion.versionNumber || this.versionNumber;
      }
      if (!file.entity.topicId) {
        file.entity.topicId = this.subsectionState?.activeTopic?.topicId;
      }
    });
  }

  private createMTMCustomFile(file, index, fileAttachForChat = null): MTMCustomFile {
    const newMTMCustomFile = new MTMCustomFile(file, index);
    newMTMCustomFile.postUploadPayload = fileAttachForChat;
    return newMTMCustomFile;
  }

  getAPIUploadPath(): string {
    return `/api/projects/${this.projectService.project.id}/sections/${this.currentOption?.section}/subsections/${this.currentOption?.subsection}/v-items/${this.currentOption?.optionId}/`;
  }

  // Methods to check if current option/version is locked
  isVersionLocked(versionNumber: number): boolean {
    if (!this.currentOption?.optionId) return false;
    const versionId = `${this.currentOption.optionId}-${versionNumber}`;
    return !!this.releaseRecord[versionId];
  }

  hasLockedVersionInOption(): boolean {
    if (!this.currentOption?.simpleVersions || !this.currentOption?.optionId) {
      return false;
    }
    return this.currentOption.simpleVersions.some((version) => {
      const versionId = `${this.currentOption!.optionId}-${version.versionNumber}`;
      return !!this.releaseRecord[versionId];
    });
  }

  areAllVersionsLocked(): boolean {
    if (!this.currentOption?.simpleVersions || !this.currentOption?.optionId) {
      return false;
    }
    return this.currentOption.simpleVersions.every((version) => {
      const versionId = `${this.currentOption!.optionId}-${version.versionNumber}`;
      return !!this.releaseRecord[versionId];
    });
  }

  isCurrentOptionLocked(): boolean {
    // Check if the current option has any locked versions or all versions are locked
    return this.hasLockedVersionInOption() || this.areAllVersionsLocked();
  }

  isCurrentVersionLocked(): boolean {
    // Check if the current version is locked
    if (!this.currentVersion?.versionNumber) return false;
    return this.isVersionLocked(this.currentVersion.versionNumber);
  }

  getPreUploadURL(version: number = 0): string {
    let versionNumber = version;
    if (versionNumber === 0) {
      versionNumber = this.currentVersion?.versionNumber || this.versionNumber;
    }
    const url = this.getAPIUploadPath() + 'preUploadFilesVR' + `?versionNumber=${versionNumber}`;
    return url;
  }

  getPostUploadURL(version: number = 0): string {
    let versionNumber = version;
    if (versionNumber === 0) {
      versionNumber = this.currentVersion?.versionNumber || this.versionNumber;
    }
    return this.getAPIUploadPath() + `postUploadFiles?versionNumber=${versionNumber}`;
  }

  monitorFiles() {
    this.newFiles = this.transferService.transferMonitor.monitor({
      newFiles: this.newFiles,
      currentFiles: this.currentFiles,
      origin: this.getPreUploadURL(),
      entity: this.currentOption,
    });
  }

  startVersionedUpload() {
    const uploadRecords: Record<number, MTMCustomFile[]> = {};
    this.newFiles
      .filter((file) => file.uploadStatusCode === TransferStatus.NEW)
      .forEach((file) => {
        if (!uploadRecords[file.entity.version]) {
          uploadRecords[file.entity.version] = [];
        }
        uploadRecords[file.entity.version].push(file);
      });
    Object.entries(uploadRecords).forEach(([version, files]) => {
      const intVersion = parseInt(version, 10);
      const request: SignedURLUploadRequest = {
        files,
        uploadType: TransferType.Chunked,
        preUploadUrl: this.getPreUploadURL(intVersion),
        postUploadUrl: this.getPostUploadURL(intVersion),
        parentId: this.currentOption.optionId,
      };
      this.transferService.uploadFiles(request);
    });
  }

  //#endregion
}
